import React, { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import ResponseModal from "@/components/ResponseModal";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import {
  Users,
  TrendingUp,
  TrendingDown,
  ExternalLink,
  Target,
  Mail,
} from "lucide-react";

export default function Competitors() {
  const { current, projects, selectProject } = useProject();
  const [overview, setOverview] = useState([]);
  const [digest, setDigest] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!current) return;
    (async () => {
      setLoading(true);
      try {
        const [o, d] = await Promise.all([
          api.get(`/projects/${current.project_id}/competitive`),
          api.get(`/projects/${current.project_id}/digest`),
        ]);
        setOverview(o.data.competitors || []);
        setDigest(d.data);
        if ((o.data.competitors || [])[0]) {
          setSelected(o.data.competitors[0].name);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [current]);

  useEffect(() => {
    if (!current || !selected) return;
    api
      .get(`/projects/${current.project_id}/competitive/${encodeURIComponent(selected)}`)
      .then((r) => setDetail(r.data));
  }, [current, selected]);

  if (!current) {
    return (
      <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
        <div className="p-8 text-sm text-zinc-500">No project selected.</div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div className="mb-8">
        <div className="label-eyebrow">Competitive intelligence</div>
        <h1 className="display mt-2 text-4xl font-black tracking-tighter">
          Competitors
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-xl">
          Who is eating your share of voice this week, which URLs are feeding
          them, and which prompts they're winning that you're missing.
        </p>
      </div>

      {!current.competitors?.length ? (
        <div
          className="card-flat p-8 text-center text-sm text-zinc-500"
          data-testid="competitors-empty"
        >
          Add competitors in <a href="/dashboard/settings" className="underline">Settings</a> to unlock competitive intel.
        </div>
      ) : loading ? (
        <div className="card-flat p-8 text-center text-sm text-zinc-500">
          Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <aside
            className="lg:col-span-3 card-flat p-3"
            data-testid="competitors-rail"
          >
            <div className="px-2 py-2 label-eyebrow">Competitors</div>
            <ul>
              {overview.map((c) => (
                <li key={c.name}>
                  <button
                    onClick={() => setSelected(c.name)}
                    data-testid={`competitor-${c.name}`}
                    className={`w-full text-left px-2 py-2 rounded-[4px] text-sm transition-colors ${
                      selected === c.name
                        ? "bg-zinc-900 text-white"
                        : "text-zinc-700 hover:bg-zinc-100"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{c.name}</span>
                      <DeltaPill value={c.delta} muted={selected === c.name} />
                    </div>
                    <div
                      className={`text-[11px] mt-0.5 ${
                        selected === c.name ? "text-zinc-300" : "text-zinc-500"
                      }`}
                    >
                      {c.share_of_voice}% share of voice
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <section className="lg:col-span-9 space-y-4" data-testid="competitor-detail">
            {detail ? (
              <>
                <header className="card-flat p-5 flex items-start justify-between">
                  <div>
                    <div className="label-eyebrow">
                      <Users size={10} className="inline mr-1 -mt-0.5" />
                      Competitor profile
                    </div>
                    <h2 className="display mt-1 text-2xl font-bold tracking-tight">
                      {detail.name}
                    </h2>
                    <div className="mt-1 text-xs text-zinc-500 mono">
                      Share of voice {detail.latest_share}% · Previous {detail.previous_share}%
                    </div>
                  </div>
                  <DeltaBadge value={detail.delta} />
                </header>

                {/* Top citing URLs */}
                <section className="card-flat p-5" data-testid="top-citing-urls">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="label-eyebrow">Top citing URLs · outreach list</div>
                      <div className="mt-1 text-sm text-zinc-500">
                        URLs AI engines cite when {detail.name} is mentioned.
                      </div>
                    </div>
                    <span className="pill mono text-[10px]">Latest run</span>
                  </div>
                  {detail.top_citing_urls?.length ? (
                    <ul className="mt-4 divide-y divide-zinc-200">
                      {detail.top_citing_urls.map((u) => (
                        <li
                          key={u.url}
                          className="py-3 flex items-center gap-3"
                        >
                          <ExternalLink size={12} className="text-zinc-500 shrink-0" />
                          <a
                            href={u.url}
                            target="_blank"
                            rel="noreferrer"
                            className="flex-1 text-sm text-zinc-800 truncate hover:underline"
                          >
                            {u.url}
                          </a>
                          <span className="mono text-xs text-zinc-500">
                            {u.citations} citation{u.citations === 1 ? "" : "s"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-4 text-sm text-zinc-500 text-center py-6">
                      No citing URLs detected yet. Kick off a run first.
                    </div>
                  )}
                </section>

                {/* Prompts they win */}
                <section className="card-flat p-5" data-testid="prompts-they-win">
                  <div className="label-eyebrow">
                    <Target size={10} className="inline mr-1 -mt-0.5" /> Prompts they win
                  </div>
                  <div className="mt-1 text-sm text-zinc-500">
                    Prompts where {detail.name} is mentioned but {detail.brand} is not.
                  </div>
                  {detail.prompts_they_win?.length ? (
                    <ul className="mt-4 space-y-2">
                      {detail.prompts_they_win.map((p) => (
                        <li
                          key={p.prompt_id}
                          className="border border-zinc-200 rounded-[4px] p-3"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                              {p.engine}
                            </span>
                            <span className="text-sm text-zinc-900 font-medium">
                              {p.prompt_text}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-zinc-600 line-clamp-2">
                            {p.response_excerpt}
                          </p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-4 text-sm text-zinc-500 text-center py-6">
                      You're matching them on every tracked prompt — nice.
                    </div>
                  )}
                </section>

                {/* Sample responses */}
                {detail.sample_results?.length > 0 && (
                  <section className="card-flat p-5" data-testid="sample-results">
                    <div className="label-eyebrow">Sample responses</div>
                    <ul className="mt-4 space-y-2">
                      {detail.sample_results.map((r) => (
                        <li key={r.result_id}>
                          <button
                            onClick={() => setResponse(r)}
                            data-testid={`sample-result-${r.result_id}`}
                            className="w-full text-left border border-zinc-200 rounded-[4px] p-3 hover:bg-[#fafafa] transition-colors"
                          >
                            <div className="mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                              {r.engine}
                            </div>
                            <div className="text-sm font-medium mt-0.5">
                              {r.prompt_text}
                            </div>
                            <div className="text-xs text-zinc-600 mt-1 line-clamp-2">
                              {r.response_excerpt}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            ) : (
              <div className="card-flat p-8 text-center text-sm text-zinc-500">
                Loading competitor detail…
              </div>
            )}
          </section>
        </div>
      )}

      {digest && (
        <section
          className="mt-8 card-flat p-5"
          data-testid="digest-preview"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="label-eyebrow">
                <Mail size={10} className="inline mr-1 -mt-0.5" />
                Weekly digest preview
              </div>
              <h3 className="display mt-1 text-xl font-bold tracking-tight">
                {digest.subject_line}
              </h3>
            </div>
            <span className="pill mono text-[10px]">
              {new Date(digest.generated_at).toLocaleDateString()}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {(digest.competitive_weekly || []).map((c) => (
              <div
                key={c.name}
                className="border border-zinc-200 rounded-[4px] p-4"
                data-testid={`digest-${c.name}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm">{c.name}</div>
                  <DeltaPill value={c.delta} />
                </div>
                <p className="mt-2 text-xs text-zinc-700">{c.headline}</p>
                <p className="mt-2 text-xs text-zinc-500">{c.recommendation}</p>
              </div>
            ))}
          </div>
          {digest.negative_mentions?.length > 0 && (
            <div className="mt-4 text-xs text-rose-700">
              ⚠ {digest.negative_mentions.length} negative mention
              {digest.negative_mentions.length === 1 ? "" : "s"} will be included
              in the email digest.
            </div>
          )}
          <div className="mt-4 text-[11px] text-zinc-500 mono">
            {digest.note}
          </div>
        </section>
      )}

      <ResponseModal response={response} onClose={() => setResponse(null)} />
    </DashboardShell>
  );
}

function DeltaPill({ value, muted }) {
  const v = Number(value || 0);
  if (v === 0)
    return (
      <span className={`mono text-[10px] ${muted ? "text-zinc-400" : "text-zinc-500"}`}>
        · 0
      </span>
    );
  const up = v > 0;
  const cls = muted
    ? "text-zinc-300"
    : up
    ? "text-emerald-600"
    : "text-rose-600";
  return (
    <span className={`mono text-[10px] ${cls}`}>
      {up ? "▲" : "▼"} {Math.abs(v).toFixed(1)}
    </span>
  );
}

function DeltaBadge({ value }) {
  const v = Number(value || 0);
  const up = v > 0;
  const down = v < 0;
  const cls = up
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : down
    ? "bg-rose-50 text-rose-700 border-rose-200"
    : "bg-zinc-50 text-zinc-700 border-zinc-200";
  const Icon = up ? TrendingUp : down ? TrendingDown : TrendingUp;
  const label = up ? "Gain" : down ? "Loss" : "Flat";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${cls}`}
      data-testid="competitor-delta"
    >
      <Icon size={12} /> {label} · {Math.abs(v).toFixed(1)}
    </span>
  );
}
